'use client';

/**
 * Host-supplied chat header actions.
 *
 * Some header actions only make sense in ONE host. Exporting a transcript to a
 * Slack Canvas needs a Slack channel picker, and picking a channel needs Hub's
 * `/api/slack/channels` — knowledge the package must not acquire, or every
 * other host (the Chrome extension, the public project chat) inherits a
 * dependency it cannot satisfy.
 *
 * So the host passes a render function and the chat header calls it. A context
 * rather than a prop chain on purpose: the header sits five components below
 * `ChatPage` and both the single-pane and tabbed paths reach it, so threading a
 * prop would touch six files to deliver one optional node.
 */

import React, { createContext, useContext } from 'react';
import type { ChatStatus } from '../lib/db/types.js';

export interface ChatHeaderActionContext {
  chatId: string;
  title: string;
  status?: ChatStatus;
}

export type RenderChatHeaderActions = (
  context: ChatHeaderActionContext,
) => React.ReactNode;

const Context = createContext<RenderChatHeaderActions | null>(null);

export function ChatHeaderActionsProvider({
  render,
  children,
}: {
  render?: RenderChatHeaderActions;
  children: React.ReactNode;
}) {
  return <Context.Provider value={render ?? null}>{children}</Context.Provider>;
}

/**
 * The host's header-action renderer, or null when the host supplied none.
 * Non-throwing: a host that never mounts the provider is the normal case.
 */
export function useChatHeaderActions(): RenderChatHeaderActions | null {
  return useContext(Context);
}
